import { type Currency } from '@vy/types';
import { withDefault } from '@vy/utils';
import React from 'react';
import { Price } from '../../../../components/design/Price';
import { PAYMENT_TOLERANCE } from '@vy/payments-types';

type PartiallyPaidableInvoice = {
  payments?: readonly { amount: number }[];
  revenueBreakdown: { total: number };
  currency: Currency;
};

function getTotalPaid(payments: PartiallyPaidableInvoice['payments']): number {
  return withDefault(
    payments?.reduce((sum, payment) => sum + payment.amount, 0),
    0,
  );
}

export function isInvoicePartiallyPaid(invoice: PartiallyPaidableInvoice): boolean {
  const totalPaid = getTotalPaid(invoice.payments);
  const invoiceTotal = invoice.revenueBreakdown.total;

  const totalPaidFormatted = Number(totalPaid.toFixed(2));
  const invoiceTotalFormatted = Number(invoiceTotal.toFixed(2));

  const hasOutstandingBalance = Math.abs(invoiceTotalFormatted - totalPaidFormatted) > PAYMENT_TOLERANCE;
  const hasPayments = totalPaidFormatted > 0;

  return hasPayments && hasOutstandingBalance;
}

export function getPartiallyPaidTooltipContent(invoice: PartiallyPaidableInvoice): React.ReactNode | null {
  if (!isInvoicePartiallyPaid(invoice)) {
    return null;
  }

  const totalPaid = getTotalPaid(invoice.payments);
  const invoiceTotal = invoice.revenueBreakdown.total;
  const missingAmount = invoiceTotal - totalPaid;
  const missingAmountFormatted = missingAmount.toFixed(2);

  return (
    <span>
      Partially paid.
      <br />
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        <Price currency={invoice.currency} sx={{ fontSize: 10 }}>
          {missingAmountFormatted}
        </Price>
        <span> outstanding</span>
      </span>
    </span>
  );
}
